import type { CdwGridColumnLookup, CdwGridColumn } from '@cdw-ae/common-data';
import type { CdwColDef } from '@cdw-ae/common-ui-components-search-grid';

/**
 * Converts a CdwGridColumnLookup + column order into AG Grid ColDef[].
 *
 * Maps: sqlName → field, displayName → headerName, headerTooltip → headerTooltip,
 * headerIcon → headerIcon (the grids read `headerIcon` to swap in
 * `customHeaderComponent`; AG Grid ignores the unknown key).
 * Wraps valueFormatter/valueGetter to bridge the signature difference
 * between the lookup's `(...args: unknown[])` and AG Grid's typed params.
 *
 * @param columnOrder - Ordered list of column names
 * @param lookup - Column definitions keyed by name
 * @param extraArgs - Additional arguments forwarded to valueFormatter/valueGetter (e.g. DateTimeSettings)
 */
export function columnLookupToColDefs(
  columnOrder: CdwGridColumn[],
  lookup: CdwGridColumnLookup,
  ...extraArgs: unknown[]
): CdwColDef[] {
  return columnOrder
    .filter((col) => col.name in lookup)
    .map((col) => {
      const entry = lookup[col.name];
      const colDef: CdwColDef = {
        field: entry.sqlName,
        headerName: entry.displayName,
        flex: entry.flex,
        minWidth: entry.minWidth,
        columnGroupShow: entry.columnGroupShow,
        hide: col.hide ?? entry.hide,
        sortable: entry.sortable,
        headerTooltip: entry.headerTooltip,
        headerIcon: entry.headerIcon,
      };

      if (entry.valueFormatter) {
        const formatter = entry.valueFormatter;
        colDef.valueFormatter = (params) =>
          String(formatter(params, ...extraArgs));
      }

      if (entry.valueGetter) {
        const getter = entry.valueGetter;
        colDef.valueGetter = (params) => getter(params, ...extraArgs);
      }

      return colDef;
    });
}
